/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { Locale, LocaleKeys } from "../../../viewModel";
import { useUnityDispatch, useUnitySelector } from "../../../viewModel";
import { selectIsBrandingFetched } from "../../../viewModel/appStartup";
import {
  openNativeApp,
  resolveAppStoreUrls,
  selectIsDialogVisible,
  selectCurrentPlatformAppStoreUrl
} from "../../../viewModel/mobileLandingDialog";
import { LocationUrls } from "../../../viewModel/navigation";
import { useNavigationService } from "../../hooks/useNavigationService";

import "./MobileLandingDialog.scss";

/**
 * MobileLandingDialog — shown on the splash and sign-in routes whenever the
 * user is on a mobile browser.
 *
 * Visibility is route-driven:
 *   - shown on / and /signin for mobile browsers
 *   - hidden on all other routes
 *
 * Not shown on:
 *   - /forgot-password or /request-new-link  (user navigated there on purpose)
 *   - /password.html                          (reset-password email deep-link)
 *   - All authenticated routes (home, guide, player, …)
 */
const MobileLandingDialog: FunctionComponent = (): JSX.Element | null => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "MobileLandingDialog";

  const dispatch = useUnityDispatch();
  const location = useLocation();
  const { navigateTo } = useNavigationService();

  const isVisible: boolean = useUnitySelector((state) =>
    selectIsDialogVisible(state, location.pathname)
  );

  const appStoreUrl: string | null = useUnitySelector(
    selectCurrentPlatformAppStoreUrl
  );

  const isBrandingFetched: boolean = useUnitySelector(selectIsBrandingFetched);

  const handleGetApp = useCallback((): void => {
    if (appStoreUrl !== null) {
      dispatch(openNativeApp(appStoreUrl));
    }
  }, [dispatch, appStoreUrl]);

  const handleForgotPassword = useCallback((): void => {
    navigateTo(LocationUrls.FORGOT_PASSWORD, false);
  }, [navigateTo]);

  useEffect((): void => {
    if (isVisible && isBrandingFetched) {
      dispatch(resolveAppStoreUrls());
    }
  }, [dispatch, isVisible, isBrandingFetched]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="mobile-landing-dialog">
      <div className="mobile-landing-dialog__backdrop" aria-hidden="true" />
      <div
        className="mobile-landing-dialog__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-dialog-title"
        aria-describedby="mobile-dialog-message"
      >
        <div className="mobile-landing-dialog__content">
          <div className="mobile-landing-dialog__title-block">
            <h2
              id="mobile-dialog-title"
              className="mobile-landing-dialog__title"
            >
              {Locale.getString(LocaleKeys.SIGN_IN_MOBILE_DIALOG_TITLE)}
            </h2>
            <div
              className="mobile-landing-dialog__divider"
              aria-hidden="true"
            />
            <p
              id="mobile-dialog-message"
              className="mobile-landing-dialog__message"
            >
              {Locale.getString(LocaleKeys.SIGN_IN_MOBILE_DIALOG_MESSAGE)}
            </p>
          </div>
        </div>
        <div className="mobile-landing-dialog__actions">
          <button
            className="mobile-landing-dialog__button mobile-landing-dialog__button--primary"
            type="button"
            onClick={handleGetApp}
          >
            {Locale.getString(LocaleKeys.SIGN_IN_MOBILE_DIALOG_GET_APP_BUTTON)}
          </button>
          <button
            className="mobile-landing-dialog__button mobile-landing-dialog__button--secondary"
            type="button"
            onClick={handleForgotPassword}
          >
            {Locale.getString(LocaleKeys.SIGN_IN_FORGOT_PASSWORD_LINK)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileLandingDialog;
